/**
 * DD narrative (Phase C.2) — generate + save the investor-owned LLM narrative.
 *
 * `useGenerateNarrative` invokes the `generate-dd-report` edge function (which
 * drafts the narrative server-side and persists it as `narrative_status='draft'`).
 * `useSaveNarrative` persists the investor's edits (`narrative_status='edited'`).
 * The narrative is never auto-published — sharing is Phase D.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';

// dd_reports narrative columns aren't in the generated types until regenerated; cast.
const db = supabase as any;

export interface DDNotablePoint {
  type: 'strength' | 'concern' | 'neutral';
  text: string;
}

export interface DDNarrativeDraft {
  executiveSummary: string;
  investmentThesis: string;
  keyRisks: string;
  notablePoints: DDNotablePoint[];
}

function invalidateCompleted(qc: ReturnType<typeof useQueryClient>) {
  // Prefix match — refreshes every investor's completed-DD list in cache.
  qc.invalidateQueries({ queryKey: ['dd-reports', 'completed'] });
}

/** Generate the narrative draft via the edge function. */
export function useGenerateNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ddReportId: string): Promise<DDNarrativeDraft> => {
      if (mode.isMock || !isSupabaseConfigured()) {
        throw new Error('Report generation is unavailable in this mode.');
      }
      const { data, error } = await supabase.functions.invoke('generate-dd-report', {
        body: { ddReportId },
      });
      if (error) throw error;
      const narrative = (data as { narrative?: DDNarrativeDraft } | null)?.narrative;
      if (!narrative) throw new Error('No narrative returned');
      return narrative;
    },
    onSuccess: () => invalidateCompleted(qc),
    onError: (err) => reportError(err, { context: 'useGenerateNarrative' }),
  });
}

/** Persist the investor's reviewed/edited narrative (status → 'edited'). */
export function useSaveNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { ddReportId: string; narrative: DDNarrativeDraft }) => {
      if (mode.isMock || !isSupabaseConfigured()) return;
      const { error } = await db
        .from('dd_reports')
        .update({
          executive_summary: p.narrative.executiveSummary || null,
          investment_thesis: p.narrative.investmentThesis || null,
          key_risks: p.narrative.keyRisks || null,
          notable_points: p.narrative.notablePoints,
          narrative_status: 'edited',
          narrative_edited_at: new Date().toISOString(),
        })
        .eq('id', p.ddReportId);
      if (error) throw error;
    },
    onSuccess: () => invalidateCompleted(qc),
    onError: (err) => reportError(err, { context: 'useSaveNarrative' }),
  });
}
